export const COLOR_OPTIONS = [
  { label: "Bleu", value: "bg-blue-400", hex: "#60A5FA" },
  { label: "Vert", value: "bg-green-400", hex: "#34D399" },
  { label: "Jaune", value: "bg-yellow-400", hex: "#FBBF24" },
  { label: "Rouge", value: "bg-red-400", hex: "#F87171" },
  { label: "Indigo", value: "bg-indigo-400", hex: "#818CF8" },
  { label: "Violet", value: "bg-purple-400", hex: "#C084FC" },
  { label: "Rose", value: "bg-pink-400", hex: "#F472B6" },
  { label: "Orange", value: "bg-orange-400", hex: "#FB923C" },
  { label: "Cyan", value: "bg-teal-400", hex: "#2DD4BF" },
  { label: "Gris", value: "bg-gray-400", hex: "#9CA3AF" },
  { label: "Noir", value: "bg-black", hex: "#020617" },
  { label: "Blanc", value: "bg-white", hex: "#FFFFFF" },
];

export const INITIAL_GROUP_FORM = {
  nom: "",
  horaire1: "",
  horaire2: "",
  annees_naissance: "",
  tarif_licence: "",
  tarif_cotisation: "",
  ordre_groupe: "",
  quota_groupe: "",
  is_active: true,
  enseignant1: "",
  enseignant2: "",
  color_groupe: "bg-blue-400",
};

export const safeJsonParse = (value, fallback = null) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

export const isDemoUser = (user) => Number(user?.role_id) === 4;

export const formatNumber = (value) =>
  new Intl.NumberFormat("fr-FR").format(Number(value || 0));

export const formatCurrency = (value) => {
  const amount = Number(value || 0);

  return amount.toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  });
};

export const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const toNumberOrEmpty = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const parsed = Number(value);
  return Number.isNaN(parsed) ? "" : parsed;
};

export const getColorOption = (value) =>
  COLOR_OPTIONS.find((color) => color.value === value) || COLOR_OPTIONS[0];

export const getColorClass = (color) => {
  if (!color || color === "bg-white") {
    return "bg-white text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:ring-slate-700";
  }

  if (color === "bg-black") {
    return "bg-slate-950 text-white dark:bg-white dark:text-slate-950";
  }

  return `${color} text-white`;
};

export const getStatusLabel = (isActive) => (isActive ? "Actif" : "Inactif");

export const getStatusClass = (isActive) =>
  isActive
    ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
    : "bg-slate-100 text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700";

export const filterAndSortGroupes = (groupes = [], filters = {}) => {
  const query = normalizeText(filters.query);
  const status = filters.status || "all";

  let result = [...groupes];

  if (status === "active") {
    result = result.filter((group) => Boolean(group.is_active));
  }

  if (status === "inactive") {
    result = result.filter((group) => !Boolean(group.is_active));
  }

  if (query) {
    result = result.filter((group) => {
      const haystack = normalizeText(
        [
          group.nom,
          group.horaire1,
          group.horaire2,
          group.annees_naissance,
          group.enseignant1,
          group.enseignant2,
        ]
          .filter(Boolean)
          .join(" ")
      );

      return haystack.includes(query);
    });
  }

  result.sort((a, b) => {
    if (filters.sortOrder === "ordre_desc") {
      return Number(b.ordre_groupe || 0) - Number(a.ordre_groupe || 0);
    }

    if (filters.sortOrder === "name_asc") {
      return normalizeText(a.nom).localeCompare(normalizeText(b.nom));
    }

    if (filters.sortOrder === "name_desc") {
      return normalizeText(b.nom).localeCompare(normalizeText(a.nom));
    }

    if (filters.sortOrder === "quota_desc") {
      return Number(b.quota_groupe || 0) - Number(a.quota_groupe || 0);
    }

    return Number(a.ordre_groupe || 0) - Number(b.ordre_groupe || 0);
  });

  return result;
};

export const buildGroupesStats = (groupes = [], filteredGroupes = []) => {
  const active = groupes.filter((group) => Boolean(group.is_active)).length;
  const inactive = groupes.length - active;

  const totalQuota = groupes.reduce(
    (total, group) => total + Number(group.quota_groupe || 0),
    0
  );

  const licenceTotal = groupes.reduce(
    (total, group) => total + Number(group.tarif_licence || 0),
    0
  );

  const cotisationTotal = groupes.reduce(
    (total, group) => total + Number(group.tarif_cotisation || 0),
    0
  );

  const avgLicence = groupes.length ? licenceTotal / groupes.length : 0;
  const avgCotisation = groupes.length ? cotisationTotal / groupes.length : 0;

  return {
    total: groupes.length,
    filtered: filteredGroupes.length,
    active,
    inactive,
    totalQuota,
    avgLicence,
    avgCotisation,
  };
};

export const normalizeGroupPayload = (formData) => ({
  ...formData,
  nom: String(formData.nom || "").trim(),
  horaire1: String(formData.horaire1 || "").trim(),
  horaire2: String(formData.horaire2 || "").trim(),
  annees_naissance: String(formData.annees_naissance || "").trim(),
  enseignant1: String(formData.enseignant1 || "").trim(),
  enseignant2: String(formData.enseignant2 || "").trim(),
  tarif_licence: toNumberOrEmpty(formData.tarif_licence),
  tarif_cotisation: toNumberOrEmpty(formData.tarif_cotisation),
  ordre_groupe: toNumberOrEmpty(formData.ordre_groupe),
  quota_groupe: toNumberOrEmpty(formData.quota_groupe),
  is_active: Boolean(formData.is_active),
  color_groupe: formData.color_groupe || "bg-blue-400",
});

export const validateGroupForm = (formData) => {
  if (!String(formData.nom || "").trim()) {
    return "Le nom du groupe est obligatoire.";
  }

  if (!String(formData.enseignant1 || "").trim()) {
    return "L'enseignant principal est obligatoire.";
  }

  const numericFields = [
    ["tarif_licence", "Le tarif licence doit être un nombre positif."],
    ["tarif_cotisation", "Le tarif cotisation doit être un nombre positif."],
    ["ordre_groupe", "L'ordre du groupe doit être un nombre positif."],
    ["quota_groupe", "Le quota du groupe doit être un nombre positif."],
  ];

  for (const [field, message] of numericFields) {
    const value = formData[field];

    if (value !== "" && Number(value) < 0) {
      return message;
    }
  }

  return null;
};

export const buildInitialFormFromGroup = (group) => {
  if (!group) return INITIAL_GROUP_FORM;

  return {
    ...INITIAL_GROUP_FORM,
    ...group,
    is_active:
      group.is_active === null || group.is_active === undefined
        ? true
        : Boolean(group.is_active),
    color_groupe: group.color_groupe || INITIAL_GROUP_FORM.color_groupe,
  };
};